import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { defaultOperationHours } from "@/lib/constant";
import { StationResultProps, StationResultSchedule } from "@/lib/type";
import { generateOperationHours } from "@/helpers/generateOperationHours";

export interface TimeInputProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  id: string;
  placeholder: string;
  prefix?: string;
  time?: string;
  setTime: (time: string) => void;
  popupClassName: string;
  showError?: boolean;
  isFocus?: boolean;
  stationLocation?: StationResultProps;
  selectedDate?: Date;
}

const scrollbarStyles = `
  .time-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .time-scrollbar::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 10px;
  }
  .time-scrollbar::-webkit-scrollbar-thumb {
    background-color: #22c55e;
    border-radius: 10px;
  }
  .time-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #22c55e #f3f4f6;
    overflow-x: hidden;
  }
  .time-header {
    text-align: center !important;
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    padding: 12px 0 8px 0;
    margin: 0 -12px 8px -12px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .time-item {
    text-align: center !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = scrollbarStyles;
  document.head.appendChild(styleElement);
}

const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  (
    {
      className,
      asChild = false,
      id,
      placeholder,
      prefix,
      time,
      setTime,
      popupClassName,
      children,
      showError = false,
      isFocus = false,
      stationLocation,
      selectedDate,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";

    const [isOpen, setIsOpen] = React.useState(false);
    const [headerText, setHeaderText] = React.useState("Open");

    React.useEffect(() => {
      setIsOpen(isFocus);
    }, [isFocus]);

    const popoverContentRef = React.useRef<HTMLDivElement>(null);
    const openHoursDividerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      setTimeout(() => {
        if (isOpen && openHoursDividerRef.current) {
          openHoursDividerRef.current.scrollIntoView({
            behavior: "instant",
            block: "start",
          });
        }
      }, 50);
    }, [isOpen, popoverContentRef.current, openHoursDividerRef.current]);
    
    function getScheduleForDate(date: Date, schedule: StationResultSchedule) {
      const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
      let openTime = "";
      let closeTime = "";
    
     if (day === 0) {
        // Sunday
        openTime = schedule?.sunday?.open ?? "Closed";
        closeTime = schedule?.sunday?.close ?? "Closed";
      } else if (day === 6) {
        // Saturday
        openTime = schedule?.saturday?.open ?? "Closed";
        closeTime = schedule?.saturday?.close ?? "Closed";
      } else {
        // Weekday
        openTime = schedule?.weekday?.open ?? "Closed";
        closeTime = schedule?.weekday?.close ?? "Closed";
      }
    
      return { openTime, closeTime };
    }

     
    function getAfterHourScheduleForDate(date: Date, schedule: StationResultSchedule) {
      const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
      let openTime: string | undefined = undefined;
      let closeTime: string | undefined = undefined;
    
     if (day === 0) {
        // Sunday
        openTime = schedule?.sunday?.after_hours_start ?? undefined;
        closeTime = schedule?.sunday?.after_hours_end ?? undefined;
      } else if (day === 6) {
        // Saturday
        openTime = schedule?.saturday?.after_hours_start ?? undefined;
        closeTime = schedule?.saturday?.after_hours_end ?? undefined;
      } else {
        // Weekday
        openTime = schedule?.weekday?.after_hours_start ?? undefined;
        closeTime = schedule?.weekday?.after_hours_end ?? undefined;
      }
    
      return { openTime, closeTime };
    }
    const { openTime, closeTime } = React.useMemo(() => {
      if (selectedDate && stationLocation?.schedule) {
        return getScheduleForDate(selectedDate, stationLocation.schedule);
      }
      return { openTime: "Closed", closeTime: "Closed" };
    }, [selectedDate, stationLocation?.schedule]);
    
    // Generate operation hours based on the station's schedule
    const operationHours = React.useMemo(() => {
      if (selectedDate && stationLocation?.schedule) {
        if (openTime !== "Closed" && closeTime !== "Closed") {
          try {
            return generateOperationHours(openTime, closeTime);
          } catch (error) {
            console.error("Error generating operation hours:", error);
            return [];
          }
        }
      }
      return [];
    }, [selectedDate, stationLocation?.schedule, openTime, closeTime]);
    
    // Generate after hours if available
    const afterHours = React.useMemo(() => {
      if (selectedDate && stationLocation?.schedule) {
        const { openTime: afterHoursStart, closeTime: afterHoursEnd } = getAfterHourScheduleForDate(selectedDate, stationLocation.schedule);
        // Only a window that stays within one day is an after-hours period; a window that
        // crosses midnight is not, so it must not add slots for the whole day.
        if (afterHoursStart && afterHoursEnd && afterHoursStart <= afterHoursEnd) {
          try {
            return generateOperationHours(afterHoursStart, afterHoursEnd);
          } catch (error) {
            console.error("Error generating after hours:", error);
            return [];
          }
        }
      }
      return [];
    }, [selectedDate, stationLocation?.schedule]);
    
    // Combine operation hours and after hours
    const allOperationHours = React.useMemo(() => {
      if (operationHours.length > 0) {
        if (afterHours.length > 0) {
          // Filter out any overlapping hours
          const combinedHours = [...operationHours];
          afterHours.forEach(hour => {
            if (!combinedHours.includes(hour)) {
              combinedHours.push(hour);
            }
          });
          return combinedHours.sort();
        }
        return operationHours;
      }
      
      if (afterHours.length > 0) {
        return afterHours;
      }
      
      // Fallback to empty array if no hours are available
      return [];
    }, [operationHours, afterHours]);

    // Determine if we need to show the "with extra charges" text
    const hasAfterHours = afterHours.length > 0;
    const hasRegularHours = operationHours.length > 0;

    // Handle scroll event to update header text
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasAfterHours || !hasRegularHours) return;
      
      const container = event.currentTarget;
      const scrollTop = container.scrollTop;
      const timeItems = container.querySelectorAll('.time-item');
      
      if (timeItems.length === 0) return;
      
      // Find the first visible time item
      for (let i = 0; i <timeItems.length; i++) {
        const item = timeItems[i] as HTMLElement;
        const itemTop = item.offsetTop;
        
        if (itemTop > scrollTop) {
          const time = item.getAttribute('data-time');
          if (time) {
            const isAfterHour = afterHours.includes(time) && !operationHours.includes(time);
            setHeaderText(isAfterHour ? "Open with extra charges" : "Open");
          }
          break;
        }
      }
    };

    // Set initial header text and auto-scroll to first regular hour
    React.useEffect(() => {
      if (hasAfterHours && hasRegularHours) {
        // Check if the first visible item is after hours
        const firstTime = allOperationHours[0];
        const isAfterHour = afterHours.includes(firstTime) && !operationHours.includes(firstTime);
        setHeaderText(isAfterHour ? "Open with extra charges" : "Open");
        
        // Auto-scroll to first regular hour when the picker opens
        if (isOpen && operationHours.length > 0) {
          const firstRegularHour = operationHours[0];
          setTimeout(() => {
            if (popoverContentRef.current) {
              // Find the header height to calculate offset
              const header = popoverContentRef.current.querySelector('.time-header');
              const headerHeight = header ? header.getBoundingClientRect().height : 0;
              
              // Find the first regular hour element
              const firstRegularElement = popoverContentRef.current.querySelector(`[data-time="${firstRegularHour}"]`);
              
              if (firstRegularElement) {
                // Calculate the exact scroll position: element's position minus header height
                const elementTop = firstRegularElement.getBoundingClientRect().top;
                const containerTop = popoverContentRef.current.getBoundingClientRect().top;
                const scrollPosition = elementTop - containerTop - headerHeight - 3; // 5px extra padding
                
                // Set the scroll position directly
                popoverContentRef.current.scrollTop = scrollPosition;
              }
            }
          }, 100); // Increased timeout to ensure elements are rendered
        }
      } else if (hasAfterHours) {
        setHeaderText("Open with extra charges");
      } else {
        setHeaderText("Open");
      }
    }, [allOperationHours, afterHours, operationHours, hasAfterHours, hasRegularHours, isOpen]);

    return (
      <Comp
        className={cn(
          `border-[1px] border-gray-300 rounded-md p-2 cursor-pointer ${isOpen ? "border-green-600 border-2" : (showError ? "border-red-500" : "")}`,
          className
        )}
        ref={ref}
        {...props}
      >
        <Popover open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
          <PopoverTrigger className="flex flex-row w-full items-center justify-center">
            {prefix == null ? <></> : <img src={prefix} />}
            <span
              id={id}
              className={`outline-0 text-center flex-1 ${time == undefined ? "text-gray-400" : "text-black font-bold"}`}
            >
              {time == undefined ? placeholder : time}
            </span>
          </PopoverTrigger>
          <PopoverContent
            ref={popoverContentRef}
            avoidCollisions={true}
            align="center"
            side="bottom"
            sideOffset={5}
            collisionPadding={16}
            className={cn(
              "w-[210px] max-h-[300px] mt-2 overflow-auto time-scrollbar rounded-xl px-3 pb-3 pt-0 shadow-lg",
              popupClassName
            )}
            onScroll={handleScroll}
          >
            {allOperationHours.length > 0 ? (
              <div className="w-full flex flex-col">
                <div
                  ref={openHoursDividerRef}
                  className="time-header"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 bg-white px-2 py-1 inline-block whitespace-nowrap">
                    {headerText}
                  </span>
                </div>
                <div className="w-full flex flex-col gap-2 pt-1">
                  {allOperationHours.map((e) => {
                    const isSelected = time === e;
                    return (
                      <div
                        key={e}
                        data-time={e}
                        className={cn(
                          "time-item py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors",
                          isSelected
                            ? "bg-green-600 text-white"
                            : "bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:bg-green-50 hover:text-green-700"
                        )}
                        onClick={() => {
                          setIsOpen(false);
                          setTime(e);
                        }}
                      >
                        {e}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center py-4 text-gray-500 text-sm whitespace-nowrap">
                No operation hours available
              </div>
            )}
          </PopoverContent>
        </Popover>
      </Comp>
    );
  }
);

TimeInput.displayName = "TimeInput";

export { TimeInput };
